// src/views/fut/Deportistas.js
import React, { useEffect, useMemo, useState } from "react";
import { ReloadOutlined } from "@ant-design/icons";
import { toast } from "react-toastify";
import {
  listDeportistas,
  updateDeportista,
  authorizeDeportista,
  INSTITUCION_ID,
} from "../../services/deportistasService";
import { ModalFichaMedica } from "../../modals/ModalFichaMedica";
import { ModalDeportista } from "../../modals/ModalDeportista";
import {
  createFichaMedica,
  updateFichaMedica,
} from "../../services/fichasMedService";
import { listPosiciones } from "../../services/posicionesService";
import { listGrupos } from "../../services/gruposService";

/* ===== Constantes ===== */
const LS_DEPORTISTAS = "deportistas_cache";

/* ===== Helpers ===== */
function clsx(...c) {
  return c.filter(Boolean).join(" ");
}

function saveLS(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify({ value, saved_at: Date.now() }));
  } catch {}
}

function readLS(key) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw).value : null;
  } catch {
    return null;
  }
}

const PLACEHOLDER_IMAGE =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200' viewBox='0 0 200 200'%3E%3Crect width='200' height='200' fill='%23f1f5f9'/%3E%3Cg fill='%233b82f6' transform='translate(100,60)'%3E%3Ccircle cx='0' cy='0' r='18'/%3E%3Crect x='-16' y='18' width='32' height='42' rx='4'/%3E%3Crect x='-20' y='18' width='12' height='35' rx='3'/%3E%3Crect x='8' y='18' width='12' height='35' rx='3'/%3E%3Crect x='-10' y='60' width='8' height='45' rx='3'/%3E%3Crect x='2' y='60' width='8' height='45' rx='3'/%3E%3C/g%3E%3Ccircle cx='118' cy='165' r='14' fill='white' stroke='%23334155' stroke-width='2'/%3E%3Cpath d='M 118,151 L 118,158 M 111,158 L 118,165 L 125,158 M 111,172 L 118,165 L 125,172' stroke='%23334155' stroke-width='2' fill='none'/%3E%3C/svg%3E";

function calculateAge(birthDate) {
  if (!birthDate) return "—";
  const today = new Date();
  const birth = new Date(birthDate);
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  return age;
}

/**
 * Procesa la URL de la foto del deportista.
 * Ahora el backend retorna URLs directas de Supabase Storage.
 * @param {string|null} fotoUrl - URL de la imagen
 * @returns {string} URL de la imagen o placeholder
 */
function processImageUrl(fotoUrl) {
  if (!fotoUrl || typeof fotoUrl !== "string") {
    return PLACEHOLDER_IMAGE;
  }

  const trimmed = fotoUrl.trim();
  if (!trimmed) {
    return PLACEHOLDER_IMAGE;
  }

  // Si es una URL válida (HTTP/HTTPS), retornarla directamente
  if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) {
    return trimmed;
  }

  // Si por alguna razón sigue siendo base64, manejarlo
  if (trimmed.startsWith("data:image/")) {
    return trimmed;
  }
  return PLACEHOLDER_IMAGE;
}

/**
 * Procesa la URL del PDF de la cédula escaneada.
 * Ahora el backend retorna URLs directas de Supabase Storage.
 * @param {string|null} docUrl - URL del PDF
 * @returns {string|null} URL del PDF o null
 */
function processPdfUrl(docUrl) {
  if (!docUrl || typeof docUrl !== "string") {
    return null;
  }

  const trimmed = docUrl.trim();
  if (!trimmed) {
    return null;
  }

  // Si es una URL válida (HTTP/HTTPS), retornarla directamente
  if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) {
    return trimmed;
  }

  // Si por alguna razón sigue siendo base64, manejarlo
  if (trimmed.startsWith("data:application/pdf")) {
    return trimmed;
  }
  return null;
}

/**
 * Determina si se debe mostrar el badge de revisión pendiente
 * @param {Object} athlete - Objeto del deportista con los campos del backend
 * @returns {boolean} true si debe mostrar el badge
 */
function shouldShowReviewBadge(athlete) {
  // Verificar las condiciones:
  // 1. cedula_scan_url diferente de null
  // 2. autorizado_dep diferente de true
  // 3. activo sea true
  
  const tieneCedulaScan = athlete.cedula_scan_url !== null && athlete.cedula_scan_url !== undefined && athlete.cedula_scan_url !== "";
  const noAutorizado = athlete.autorizado_dep !== true;
  const estaActivo = athlete.activo === true;
  
  return tieneCedulaScan && noAutorizado && estaActivo;
}

/**
 * Normaliza los datos del deportista para la UI.
 * Ahora maneja URLs directas de Supabase Storage en lugar de base64.
 * @param {Object} athlete - Datos del deportista desde el backend
 * @returns {Object} Deportista normalizado
 */
function normalizeAthlete(athlete) {
  const autorizado =
    typeof athlete.autorizado_dep === "boolean" ? athlete.autorizado_dep : null;

  let docEstado = "Pendiente";
  if (autorizado === true) docEstado = "Autorizado";
  else if (autorizado === false) docEstado = "Rechazado";

  // 🔹 Verificar si debe mostrar badge de revisión pendiente
  const mostrarBadgeRevision = shouldShowReviewBadge(athlete);

  return {
    // Datos base
    id: athlete.id,
    institucion_id: athlete.institucion_id || INSTITUCION_ID,
    representante_id: athlete.representante_id,
    posicion_id: athlete.posicion_id,
    grupo_id: athlete.grupo_id,
    cedula: athlete.cedula || "",
    nombres: athlete.nombres ?? "",
    apellidos: athlete.apellidos ?? "",
    email: athlete.email ?? "",
    fecha_nacimiento: athlete.fecha_nacimiento,
    direccion: athlete.direccion ?? "",
    telefono: athlete.telefono ?? "",
    genero: athlete.genero ?? "Masculino",
    observaciones: athlete.observaciones ?? "",

    // Docs / autorización
    autorizado_dep: autorizado,
    observacion_ced_fot: athlete.observacion_ced_fot ?? "",
    docEstado,
    
    // URLs directas de Supabase Storage
    imageUrl: processImageUrl(athlete.foto_url),
    cedulaUrl: processPdfUrl(athlete.cedula_scan_url),

    // Datos calculados para visualización
    edad: calculateAge(athlete.fecha_nacimiento),
    estado: athlete.activo ? "Activo" : "Inactivo",
    grupo: athlete.grupos?.nombre ?? "Sin grupo",
    posicion: athlete.posiciones?.nombre ?? "Sin posición",
    entrenador: athlete.entrenador ?? "—",
    representante: athlete.representante ?? "—",
    fichaMedica: !!athlete.fichas_medicas?.id,
    asistenciaMes: athlete.asistenciaMes ?? 0,
    registradoEn: athlete.creado_en?.slice(0, 10) ?? "—",
    ficha: athlete.fichas_medicas?.id || "",
    // badge de revisión pendiente
    mostrarBadgeRevision,
  };
}

// === UI Components ===
function AthleteImage({ src, alt, className }) {
  const [imgSrc, setImgSrc] = useState(src);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    setImgSrc(src);
    setHasError(false);
  }, [src]);

  const handleError = () => {
    if (!hasError) {
      console.warn("Error cargando imagen, usando placeholder");
      setImgSrc(PLACEHOLDER_IMAGE);
      setHasError(true);
    }
  };

  return (
    <img
      src={imgSrc}
      alt={alt}
      className={className}
      onError={handleError}
      loading="lazy"
    />
  );
}

function Badge({ children, variant = "default" }) {
  const variants = {
    default: "bg-slate-100 text-slate-700 border-slate-200",
    success: "bg-emerald-50 text-emerald-700 border-emerald-200",
    danger: "bg-rose-50 text-rose-700 border-rose-200",
    muted: "bg-slate-50 text-slate-500 border-slate-200",
    warning: "bg-amber-50 text-amber-700 border-amber-200",
  };

  const isStatusBadge = children === "Activo" || children === "Inactivo";
  const isActive = children === "Activo";

  return (
    <span
      className={clsx(
        "inline-flex items-center gap-1 rounded-full border text-xs px-2 py-1",
        variants[variant]
      )}
    >
      {isStatusBadge && (
        <span
          className={clsx(
            "w-1.5 h-1.5 rounded-full",
            isActive ? "bg-emerald-500" : "bg-rose-500"
          )}
        />
      )}
      {children}
    </span>
  );
}

// === Main View (solo ver + editar) ===
export default function AthletesView() {
  const [athletes, setAthletes] = useState([]);
  const [posiciones, setPosiciones] = useState([]);
  const [grupos, setGrupos] = useState([]);
  const [query, setQuery] = useState("");
  const [onlyActive, setOnlyActive] = useState(true);
  const [onlyAuthorized, setOnlyAuthorized] = useState(true);
  const [selected, setSelected] = useState(null);
  const [mode, setMode] = useState(null);
  const [viewMode, setViewMode] = useState("cards");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingFicha, setSavingFicha] = useState(false);
  const [error, setError] = useState("");

  const loadAthletes = async () => {
    try {
      setLoading(true);
      setError("");
      const data = await listDeportistas();
      const normalized = (data || []).map(normalizeAthlete);
      setAthletes(normalized);
      saveLS(LS_DEPORTISTAS, normalized);
    } catch (e) {
      console.error("[DEPORTISTAS][LOAD][ERROR]", e);
      setError(e?.message || "Error cargando deportistas");
      toast.error(e?.message || "Error cargando deportistas");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const cached = readLS(LS_DEPORTISTAS);
    if (cached) {
      setAthletes(cached);
      setLoading(false);
    }

    let mounted = true;

    const loadInitialData = async () => {
      setLoading(true);
      try {
        const [athletesData, posicionesData, gruposData] = await Promise.all([
          listDeportistas(),
          listPosiciones(),
          listGrupos(),
        ]);

        if (!mounted) return;
        const normalized = (athletesData || []).map(normalizeAthlete);
        setAthletes(normalized);
        setPosiciones(posicionesData || []);
        setGrupos(gruposData || []);
        saveLS(LS_DEPORTISTAS, normalized);
      } catch (e) {
        console.error("[DEPORTISTAS][INIT][ERROR]", e);
        if (mounted) {
          setError(e?.message || "Error cargando datos");
          toast.error("Error al cargar los datos iniciales");
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadInitialData();

    return () => {
      mounted = false;
    };
  }, []);

  const filtered = useMemo(() => {
    const term = (query || "").toString().toLowerCase();
    return athletes.filter((a) => {
      const haystack = `${a.nombres || ""} ${a.apellidos || ""} ${
        a.grupo || ""
      } ${a.entrenador || ""} ${a.representante || ""}`.toLowerCase();
      const matches = haystack.includes(term);
      const active = onlyActive ? a.estado === "Activo" : true;
      const authorized = onlyAuthorized ? a.autorizado_dep === true : a.autorizado_dep === false;
      return matches && active && authorized;
    });
  }, [athletes, query, onlyActive, onlyAuthorized]);

  function openEdit(a) {
    setSelected(a);
    setMode("edit");
  }

  function openFicha(a) {
    setSelected(a);
    setMode("ficha");
  }

  function closeModal() {
    setMode(null);
    setSelected(null);
  }

  async function handleSaveEdit(updated) {
    if (!updated?.id) {
      toast.error("No se encontró el ID del deportista.");
      return;
    }

    try {
      setSaving(true);

      // 1) Actualizar datos generales (sin tocar autorización)
      const basePayload = {
        id: updated.id,
        grupo_id: updated.grupo_id,
        posicion_id: updated.posicion_id,
        nombres: updated.nombres,
        apellidos: updated.apellidos,
        email: updated.email,
        fecha_nacimiento: updated.fecha_nacimiento,
        direccion: updated.direccion,
        telefono: updated.telefono,
        genero: updated.genero,
        observaciones: updated.observaciones,
      };

      // 🔹 Si marcó que la foto o la cédula están mal, enviar campos vacíos
      if (updated.clearFoto) {
        basePayload.foto_url = "";
      }
      if (updated.clearCedulaPdf) {
        basePayload.cedula_scan_url = "";
      }

      await updateDeportista(basePayload);

      const originalAuth =
        typeof selected?.autorizado_dep === "boolean"
          ? selected.autorizado_dep
          : null;
      const newAuth =
        typeof updated.autorizado_dep === "boolean"
          ? updated.autorizado_dep
          : null;

      // 2) Autorizar / rechazar usando SOLO el payload que indicaste
      if (typeof newAuth === "boolean") {
        const authPayload = {
          id: updated.id,
          autorizado_dep: newAuth,
          observacion_ced_fot: updated.observacion_ced_fot || "",
        };
        
        await authorizeDeportista(authPayload);
      } else {
      }

      // 4) Si APRUEBA por primera vez → crear pago
      if (newAuth === true && originalAuth !== true) {
        const now = new Date();
        const due = new Date(now);
        due.setMonth(due.getMonth() + 1);

        const year = now.getFullYear();
        const yyyy = String(year);
        const mm = String(due.getMonth() + 1).padStart(2, "0");
        const dd = String(due.getDate()).padStart(2, "0");

        const representanteId = selected?.representante_id;

        const orderBody = {
          representante_id: representanteId,
          deportista_id: updated.id,
          concepto: "Primer pago matrícula + primer mes (40 USD)",
          monto: 0,
          periodo: yyyy,
          fecha_vencimiento: `${yyyy}-${mm}-${dd}`,
          estado: "pending",
          tipo_pago: null,
          comprobante_url: null,
          confirmado_por: null,
          fecha_confirmacion: null,
          comentario: null,
        };
      }
      // 5) Recargar lista
      await loadAthletes();
      toast.success("Deportista actualizado correctamente");
      closeModal();
    } catch (e) {
      console.error("┗━━━━━━━━ DEP - handleSaveEdit ERROR ━━━━━━━━", e);
      toast.error(e?.message || "No se pudo actualizar el deportista");
    } finally {
      setSaving(false);
    }
  }

  async function handleSaveFicha(updatedFicha) {
    try {
      setSavingFicha(true);

      if (updatedFicha.id) {
        await updateFichaMedica(updatedFicha.id, updatedFicha);
        toast.success("Ficha médica actualizada correctamente");
      } else {
        await createFichaMedica(updatedFicha);
        toast.success("Ficha médica creada correctamente");
      }

      await loadAthletes();
      closeModal();
    } catch (error) {
      console.error("Error guardando ficha médica:", error);
      toast.error(error?.message || "No se pudo guardar la ficha médica");
    } finally {
      setSavingFicha(false);
    }
  }

  return (
    <div className="mx-auto max-w-7xl p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
        <div>
          <h1 className="text-xl md:text-2xl font-semibold">Deportistas</h1>
          <p className="text-slate-600 text-sm">
            Visualización y edición de deportistas de la institución.
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row md:items-center gap-3 mb-6">
        <div className="flex-1">
          <input
            className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-4 focus:ring-blue-100"
            placeholder="Buscar por nombre, grupo, entrenador o representante"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>

        <label className="inline-flex items-center gap-2.5 px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm text-slate-700 select-none cursor-pointer hover:bg-slate-50 transition-colors">
          <div className="relative flex items-center">
            <input
              type="checkbox"
              checked={onlyActive}
              onChange={(e) => setOnlyActive(e.target.checked)}
              className="peer sr-only"
            />
            <div className="w-5 h-5 rounded border-2 border-slate-300 bg-white peer-checked:bg-blue-600 peer-checked:border-blue-600 transition-all flex items-center justify-center">
              {onlyActive && (
                <svg
                  className="w-3.5 h-3.5 text-white"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={3}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              )}
            </div>
          </div>
          <span className="font-medium">Mostrar solo activos</span>
        </label>

        <label className="inline-flex items-center gap-2.5 px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm text-slate-700 select-none cursor-pointer hover:bg-slate-50 transition-colors">
          <div className="relative flex items-center">
            <input
              type="checkbox"
              checked={onlyAuthorized}
              onChange={(e) => setOnlyAuthorized(e.target.checked)}
              className="peer sr-only"
            />
            <div className="w-5 h-5 rounded border-2 border-slate-300 bg-white peer-checked:bg-emerald-600 peer-checked:border-emerald-600 transition-all flex items-center justify-center">
              {onlyAuthorized && (
                <svg
                  className="w-3.5 h-3.5 text-white"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={3}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              )}
            </div>
          </div>
          <span className="font-medium">{onlyAuthorized ? "Solo autorizados" : "Solo no autorizados"}</span>
        </label>

        <div className="flex items-center gap-1 p-1 bg-slate-100 rounded-lg">
          <button
            onClick={() => setViewMode("cards")}
            className={clsx(
              "px-3 py-1.5 rounded-md text-sm font-medium transition-all",
              viewMode === "cards"
                ? "bg-white text-blue-600 shadow-sm"
                : "text-slate-600 hover:text-slate-900"
            )}
            title="Vista de tarjetas"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
              />
            </svg>
          </button>
          <button
            onClick={() => setViewMode("list")}
            className={clsx(
              "px-3 py-1.5 rounded-md text-sm font-medium transition-all",
              viewMode === "list"
                ? "bg-white text-blue-600 shadow-sm"
                : "text-slate-600 hover:text-slate-900"
            )}
            title="Vista de lista"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h.01M4 12h.01M4 18h.01M8 6h13M8 12h13M8 18h13"
              />
            </svg>
          </button>
        </div>
        
        <button
          onClick={loadAthletes}
          disabled={loading}
          className="inline-flex items-center gap-2 rounded-xl bg-slate-100 text-slate-700 px-3 py-2 text-sm font-medium hover:bg-slate-200 active:scale-[.99] transition disabled:opacity-50 disabled:cursor-not-allowed"
          title="Recargar datos"
        >
          <ReloadOutlined className="text-base" spin={loading} />
        </button>
      </div>

      {error && (
        <div className="mb-4 rounded-xl border border-rose-200 bg-rose-50 text-rose-700 px-3 py-2 text-sm">
          {error}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div
              key={i}
              className="h-80 rounded-2xl border border-slate-200 bg-white overflow-hidden"
            >
              <div className="animate-pulse h-full bg-slate-100" />
            </div>
          ))}
        </div>
      )}

      {/* Cards View */}
      {!loading && viewMode === "cards" && (
        <>
          {filtered.length === 0 ? (
            <div className="text-center py-16 px-4">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-slate-100 mb-4">
                <svg
                  className="w-8 h-8 text-slate-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-1">
                No se encontraron deportistas
              </h3>
              <p className="text-sm text-slate-500">
                {query
                  ? "Intenta ajustar los filtros de búsqueda"
                  : "No hay deportistas registrados"}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filtered.map((athlete) => (
                <article
                  key={athlete.id}
                  className="flex flex-col overflow-hidden rounded-2xl shadow-sm border border-slate-200 bg-white hover:shadow-md transition relative"
                >
                  {athlete.mostrarBadgeRevision && (
                    <div className="absolute top-2 left-2 z-10">
                      <div className="w-3 h-3 rounded-full bg-red-500 shadow-md" title="Requiere revisión" />
                    </div>
                  )}
                  <div className="p-4">
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <AthleteImage
                          src={athlete.imageUrl}
                          alt={`${athlete.nombres} ${athlete.apellidos}`}
                          className="w-16 h-16 rounded-xl object-cover border border-slate-200 bg-slate-50 flex-shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <h3 className="text-base font-semibold truncate">
                            {athlete.nombres} {athlete.apellidos}
                          </h3>
                          <div className="mt-1 flex items-center gap-2 flex-wrap">
                            <Badge
                              variant={
                                athlete.estado === "Activo"
                                  ? "success"
                                  : "danger"
                              }
                            >
                              {athlete.estado}
                            </Badge>
                            <Badge variant="muted">{athlete.grupo}</Badge>
                            {athlete.autorizado_dep === true ? (
                              <Badge variant="success">Verificada</Badge>
                            ) : (
                              <Badge variant="danger">Pendiente</Badge>
                            )}
                          </div>
                        </div>
                      </div>

                      {athlete.estado === "Activo" && (
                        <button
                          onClick={() => openEdit(athlete)}
                          className="rounded-lg border border-slate-200 hover:bg-slate-100 p-2 transition"
                          title="Editar deportista"
                          aria-label="Editar"
                        >
                          <svg
                            className="w-4 h-4 text-slate-700"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                            />
                          </svg>
                        </button>
                      )}
                    </div>

                    <div className="text-sm text-slate-700 grid grid-cols-2 gap-y-2 gap-x-3">
                      <span className="text-slate-500">Edad:</span>
                      <span>{athlete.edad} años</span>
                      <span className="text-slate-500">Posición:</span>
                      <span className="truncate">{athlete.posicion}</span>
                      <span className="text-slate-500">Ficha médica:</span>
                      <div>
                        {athlete.fichaMedica ? (
                          <Badge variant="success">✓ Completa</Badge>
                        ) : (
                          <Badge variant="danger">✗ Pendiente</Badge>
                        )}
                      </div>
                      <span className="text-slate-500">Cédula:</span>
                      <span className="truncate">{athlete.cedula || "—"}</span>
                      <span className="text-slate-500">Teléfono:</span>
                      <span>{athlete.telefono || "—"}</span>
                      <span className="text-slate-500">Registrado:</span>
                      <span>{athlete.registradoEn}</span>
                    </div>

                    <button
                      onClick={() => openFicha(athlete)}
                      className="w-full inline-flex items-center justify-center rounded-xl border border-emerald-600 text-emerald-700 px-3 py-2 text-sm hover:bg-emerald-50 hover:border-emerald-400 active:scale-[.99] transition mt-4"
                    >
                      Ficha médica
                    </button>
                  </div>
                </article>
              ))}
            </div>
          )}
        </>
      )}

      {/* List View */}
      {!loading && viewMode === "list" && (
        <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Deportista
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Edad
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Posición
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Grupo
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Cédula
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Ficha Médica
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Verificación
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Estado
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {filtered.map((athlete) => (
                  <tr
                    key={athlete.id}
                    className="hover:bg-slate-50 transition-colors"
                  >
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        <AthleteImage
                          src={athlete.imageUrl}
                          alt={`${athlete.nombres} ${athlete.apellidos}`}
                          className="w-10 h-10 rounded-lg object-cover border border-slate-200 bg-slate-50"
                        />
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-slate-900 truncate">
                            {athlete.nombres} {athlete.apellidos}
                          </div>
                          <div className="text-xs text-slate-500 truncate">
                            {athlete.email || "Sin email"}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-700">
                      {athlete.edad} años
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-700">
                      {athlete.posicion}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <Badge variant="muted">{athlete.grupo}</Badge>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-700">
                      {athlete.cedula || "—"}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {athlete.fichaMedica ? (
                        <Badge variant="success">✓ Completa</Badge>
                      ) : (
                        <Badge variant="danger">✗ Pendiente</Badge>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {/* 🔹 Verificación en la tabla también */}
                      {athlete.autorizado_dep === true ? (
                        <Badge variant="success">Cuenta verificada</Badge>
                      ) : athlete.autorizado_dep === false ? (
                        <Badge variant="danger">Documentos rechazados</Badge>
                      ) : (
                        <Badge variant="warning">Verificación pendiente</Badge>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <Badge
                        variant={
                          athlete.estado === "Activo" ? "success" : "danger"
                        }
                      >
                        {athlete.estado}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => openFicha(athlete)}
                          className="inline-flex items-center justify-center rounded-lg border border-emerald-600 text-emerald-700 px-3 py-1.5 text-xs font-medium hover:bg-emerald-50 hover:border-emerald-400 transition"
                          title="Ver ficha médica"
                        >
                          Ficha
                        </button>
                        <button
                          onClick={() => openEdit(athlete)}
                          className="inline-flex items-center justify-center rounded-lg border border-blue-600 text-blue-600 px-3 py-1.5 text-xs font-medium hover:bg-blue-50 hover:border-blue-400 hover:text-blue-500 transition"
                          title="Editar deportista"
                        >
                          Editar
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filtered.length === 0 && (
            <div className="text-center py-12 text-slate-500">
              <p className="text-sm">No se encontraron deportistas</p>
            </div>
          )}
        </div>
      )}

      <ModalDeportista
        open={mode === "edit" && selected !== null}
        athlete={selected}
        posiciones={posiciones}
        grupos={grupos}
        saving={saving}
        onCancel={closeModal}
        onSave={handleSaveEdit}
      />

      <ModalFichaMedica
        open={mode === "ficha" && selected !== null}
        title={
          selected
            ? `Ficha médica — ${selected.nombres} ${selected.apellidos}`
            : "Ficha médica"
        }
        initialFicha={{
          deportista_id: selected?.id || "",
          ficha_medica_id: selected?.ficha || "",
          nombres: selected?.nombres || "",
          apellidos: selected?.apellidos || "",
          nombre_completo: selected
            ? `${selected.nombres || ""} ${selected.apellidos || ""}`.trim()
            : "",
          cedula: selected?.cedula || "",
          grupo: selected?.grupo || "",
          posicion: selected?.posicion || "",
          edad: selected?.edad ?? null,
        }}
        saving={savingFicha}
        onCancel={closeModal}
        onSave={handleSaveFicha}
      />
    </div>
  );
}
